import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import './App.css';
import { useQueryClient } from '@tanstack/react-query';
import Login from './components/Login';
import AuthenticatedLayout from './components/AuthenticatedLayout';
import { Toaster, toast } from './components/ui/sonner';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from './components/ui/alert-dialog';
import { ThemeProvider } from './contexts/ThemeContext';
import { PreferencesProvider } from './contexts/PreferencesContext';
import { AccountProvider, useAccount } from './contexts/AccountContext';
import { PermissionProvider } from './contexts/PermissionContext';
import { setupSubscriptionInterceptor, checkSubscriptionStatus } from './utils/subscriptionCheck';
import { getAuthHeaders, getApiHeaders, clearAuthTokens as clearAuthTokensFromStorage, startTokenRefreshTimer } from './utils/auth';
import crossTabSync from './utils/crossTabSync';
import { QueryProvider } from './providers/QueryProvider';
import { BACKEND_URL } from './config/api';
import ErrorBoundary from './components/ErrorBoundary';
import { PageLoader } from './components/ui/Loading';
import { useSubscriptionPrompt, SubscribePromptModal } from './components/GracePeriodBanner';
import VerificationBanner from './components/VerificationBanner';
import ActionBlockedModal from './components/common/ActionBlockedModal';
import { useTierLimitHandler } from './hooks/useTierLimitHandler';
import ScrollToTop from './components/ScrollToTop';

// Lazy load heavy components for better performance (reduces initial bundle by ~860KB)
const Dashboard = lazy(() => import('./components/Dashboard'));
const VehicleManagement = lazy(() => import('./components/VehicleManagementWithTabs'));
const CoHostManagement = lazy(() => import('./components/CoHostManagementWithViews'));
const CoHostDetail = lazy(() => import('./components/CoHostDetail'));
const CohostFinanceHub = lazy(() => import('./pages/CohostFinanceHub'));
const ExpenseBreakdownPage = lazy(() => import('./pages/ExpenseBreakdownPage'));
const EarningsBreakdownPage = lazy(() => import('./pages/EarningsBreakdownPage'));
const NetProfitBreakdownPage = lazy(() => import('./pages/NetProfitBreakdownPage'));
const SettingsRouter = lazy(() => import('./components/settings/SettingsRouter'));
const OnboardingSimple = lazy(() => import('./components/OnboardingSimple'));
const PublicReport = lazy(() => import('./components/PublicReport'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const LandingPage = lazy(() => import('./pages/Landing/LandingPage'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const AffiliateTerms = lazy(() => import('./pages/AffiliateTerms'));
const AffiliateApply = lazy(() => import('./pages/AffiliateApply'));
const AffiliateDashboard = lazy(() => import('./pages/AffiliateDashboard'));
const AffiliateAdmin = lazy(() => import('./pages/AffiliateAdmin'));
const TransparentAccounting = lazy(() => import('./pages/TransparentAccounting'));
const HelpCenter = lazy(() => import('./pages/HelpCenter'));
const InviteAccept = lazy(() => import('./pages/InviteAccept'));
const PublicSigningPage = lazy(() => import('./pages/PublicSigningPage'));
const BankTransactionsPage = lazy(() => import('./pages/BankTransactionsPage'));
const ROICalculator = lazy(() => import('./pages/ROICalculator'));
const SharedROIRedirect = lazy(() => import('./pages/SharedROIRedirect'));
const MigrationServicePage = lazy(() => import('./pages/MigrationServicePage'));
// Monday.com Replacement Pages - Per VEHICLES_PAGE_REFACTORING_PLAN.md:
// CheckoutPipelinePage and ComplianceDashboardPage are now integrated into VehicleManagementWithTabs
// TodayOperationsDashboard is now integrated into OperationsTabContent
// No standalone lazy imports needed - they're loaded via the unified tabs

// Domain-based routing helper
// www.turnover-manager.com → Landing page domain
// app.turnover-manager.com → Application domain
const isLandingDomain = () => {
  const hostname = window.location.hostname;
  return hostname === 'www.turnover-manager.com' ||
    hostname === 'turnover-manager.com' ||
    hostname === 'landing.turnover-manager.com';
};

const _isAppDomain = () => {
  const hostname = window.location.hostname;
  return hostname === 'app.turnover-manager.com' ||
    hostname === 'localhost' ||
    hostname.includes('vercel.app'); // For preview deployments
};

// Redirect component for external URLs (React Router Navigate doesn't support external URLs)
const ExternalRedirect = ({ to }) => {
  React.useEffect(() => {
    window.location.href = to;
  }, [to]);
  return <PageLoader />;
};


function AppContent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { loading: accountContextLoading, refreshAccounts } = useAccount();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [accessChecked, setAccessChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(true);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // Hook for subscription error prompts (Free model — no grace periods)
  const { showPrompt: showSubscriptionPrompt, closePrompt: closeSubscriptionPrompt } = useSubscriptionPrompt();

  // Hook for tier limit exceeded handling (global 402 error modal)
  const { 
    showBlockedModal, 
    setShowBlockedModal, 
    limitDetails, 
    blockedAction 
  } = useTierLimitHandler();

  // Check if we're on the landing page (either landing domain or / path on app domain)
  const isLandingPage = isLandingDomain() || window.location.pathname === '/';

  useEffect(() => {
    // Setup subscription interceptor when component mounts
    setupSubscriptionInterceptor(navigate);
    checkAuthStatus();
  }, [navigate]);

  // Listen for session expiry logout events from authenticatedFetch
  useEffect(() => {
    const handleSessionExpired = () => {
      clearAuthTokens({ broadcast: false }); // Don't broadcast back
      setNeedsOnboarding(false);
      setIsSidebarCollapsed(false);
      setHasAccess(true);
      setAccessChecked(false);
      navigate('/login');
    };

    window.addEventListener('auth:logout', handleSessionExpired);
    return () => {
      window.removeEventListener('auth:logout', handleSessionExpired);
    };
  }, [navigate]);

  // Listen for logout events from other tabs (cross-tab sync)
  useEffect(() => {
    const unsubscribe = crossTabSync.onLogout(() => {
      // Cross-tab logout detected
      clearAuthTokens({ broadcast: false }); // Don't broadcast back
      setNeedsOnboarding(false);
      setIsSidebarCollapsed(false);
      setHasAccess(true);
      setAccessChecked(false);
      setUser(null);
      toast.info('You were signed out from another tab');
      navigate('/login');
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (isAuthenticated && user) {
      checkOnboardingStatus();
    }
  }, [isAuthenticated, user]);

  const checkAuthStatus = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      // CRITICAL: Use getAuthHeaders() to include X-Account-Id for team members
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const user = await response.json();
        setUser(user);
        setIsAuthenticated(true);
        // BUG-010 FIX: Start proactive token refresh to prevent session loss
        startTokenRefreshTimer();
        await checkAccess();
      } else {
        // Clear invalid token
        clearAuthTokens();
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      clearAuthTokens();
    }

    setLoading(false);
  };



  // FIX: Use the imported clearAuthTokens which broadcasts to other tabs
  // Also clear React Query cache on logout
  const clearAuthTokens = (options = {}) => {
    clearAuthTokensFromStorage(options);
    queryClient.clear();
    setUser(null);
    setIsAuthenticated(false);
  };

  const checkOnboardingStatus = async () => {
    setOnboardingLoading(true);
    try {
      // First check if user has already completed onboarding (stored in user profile)
      // This prevents onboarding from showing again after data deletion
      if (user?.hasCompletedOnboarding) {
        setNeedsOnboarding(false);
        setOnboardingLoading(false);
        return;
      }

      // For users who haven't completed onboarding yet, check if they have any cars
      // IMPORTANT: Use getAuthHeaders() to include X-Account-Id for team members
      const response = await fetch(`${BACKEND_URL}/api/cars`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const result = await response.json();
        const cars = result.data || [];
        // Only show onboarding if user hasn't completed it AND has no cars
        setNeedsOnboarding(cars.length === 0);
      } else {
        // If check fails, assume no onboarding needed
        setNeedsOnboarding(false);
      }
    } catch (error) {
      console.error('Onboarding status check failed:', error);
      // If check fails, assume no onboarding needed
      setNeedsOnboarding(false);
    }
    setOnboardingLoading(false);
  };

  const handleLogin = async (userData, tokens) => {
    localStorage.setItem('accessToken', tokens.accessToken);
    if (tokens.refreshToken) {
      localStorage.setItem('refreshToken', tokens.refreshToken);
    }
    setUser(userData);
    setIsAuthenticated(true);
    // BUG-010 FIX: Start proactive token refresh to prevent session loss
    startTokenRefreshTimer();
    // Refresh account context now that user is authenticated
    await refreshAccounts();
    checkAccess();
  };

  const handleLogout = async () => {
    // Show full-screen logout confirmation modal
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    // Clear tokens and reset state
    clearAuthTokens();
    setNeedsOnboarding(false);
    setIsSidebarCollapsed(false);
    setHasAccess(true);
    setAccessChecked(false);
    setShowLogoutModal(false);
    toast.success('Signed out successfully');
    // Navigate to login page after logout
    navigate('/login');
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  // Direct logout without confirmation (for subscription required page)
  const handleDirectLogout = async () => {
    clearAuthTokens();
    setNeedsOnboarding(false);
    setIsSidebarCollapsed(false);
    setHasAccess(true);
    setAccessChecked(false);
    navigate('/login');
  };

  const checkAccess = async () => {
    // FIX: Wait for account context to finish loading before checking subscription
    // This ensures localStorage activeAccountId is corrected before we check subscription status
    if (accountContextLoading) {
      return; // Will be called again via useEffect when loading completes
    }
    
    try {
      const status = await checkSubscriptionStatus();
      setHasAccess(Boolean(status?.hasAccess));
    } catch {
      // fail-open to avoid accidental lockout
      setHasAccess(true);
    } finally {
      setAccessChecked(true);
    }
  };
  
  // Re-check access when account context finishes loading (fixes stale localStorage race condition)
  useEffect(() => {
    if (isAuthenticated && !accountContextLoading && !accessChecked) {
      checkAccess();
    }
  }, [isAuthenticated, accountContextLoading, accessChecked]);

  const SubscriptionRequired = () => (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-200 flex items-center justify-center px-4">
      <div className="max-w-lg w-full bg-white shadow-xl rounded-2xl p-8 border border-slate-200 text-center space-y-4">
        <div className="w-16 h-16 mx-auto rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-500 text-2xl">
          <i className="fas fa-lock"></i>
        </div>
        <h2 className="text-2xl font-bold text-slate-900">Subscription required</h2>
        <p className="text-sm text-slate-600">
          Your subscription is inactive. Please update your billing to continue.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            className="px-5 py-3 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition"
            onClick={() => navigate('/pricing')}
          >
            View Plans
          </button>
          <button
            className="px-5 py-3 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 transition"
            onClick={handleDirectLogout}
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );

  const handleOnboardingComplete = async () => {
    // Mark onboarding as complete in the backend
    try {
      const _token = localStorage.getItem('accessToken');
      await fetch(`${BACKEND_URL}/api/auth/complete-onboarding`, {
        method: 'POST',
        headers: getApiHeaders()
      });
      // Update local user state
      setUser(prev => prev ? { ...prev, hasCompletedOnboarding: true } : prev);
    } catch (error) {
      console.error('Failed to mark onboarding complete:', error);
    }
    setNeedsOnboarding(false);
  };

  const handleSidebarToggle = () => {
    setIsSidebarCollapsed((prev) => !prev);
  };

  if (loading || onboardingLoading || (isAuthenticated && !accessChecked)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <PermissionProvider>
    <div className={`App ${!isLandingPage ? 'min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 text-slate-900' : ''}`}>
      {/* Subscription prompts handled by useSubscriptionPrompt hook */}

      {/* Email Verification Banner - shows when user hasn't verified their email */}
      {isAuthenticated && user && !isLandingPage && (
        <VerificationBanner 
          user={user} 
          onVerificationComplete={() => {
            // Refresh user data to update emailVerified status and hide banner
            setUser(prev => prev ? { ...prev, emailVerified: true } : prev);
          }}
        />
      )}

      {/* Subscribe Prompt Modal - shows when user tries a subscription-blocked action */}
      <SubscribePromptModal
        isOpen={showSubscriptionPrompt}
        onClose={closeSubscriptionPrompt}
      />

      {/* Action Blocked Modal - shows when user tries to perform blocked action due to tier limits */}
      <ActionBlockedModal
        open={showBlockedModal}
        onOpenChange={setShowBlockedModal}
        action={blockedAction}
        limitDetails={limitDetails}
      />

      {/* Logout Confirmation Modal */}
      <AlertDialog open={showLogoutModal} onOpenChange={(open) => { if (!open) cancelLogout(); }}>
        <AlertDialogContent className="max-w-md rounded-2xl p-8">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-6">
              <i className="fas fa-sign-out-alt text-2xl text-red-600"></i>
            </div>
            <AlertDialogHeader className="space-y-2 mb-8">
              <AlertDialogTitle className="text-2xl font-bold text-slate-900 text-center">Sign Out?</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-600 text-center">
                Are you sure you want to sign out of your account?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-row gap-3 sm:justify-center">
              <AlertDialogCancel
                onClick={cancelLogout}
                className="flex-1 h-12 sm:h-12 px-6 sm:px-6 rounded-xl border border-slate-200 bg-white text-slate-700 font-semibold hover:bg-slate-50 transition-colors mt-0"
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                data-testid="confirm-logout-button"
                onClick={confirmLogout}
                className="flex-1 h-12 sm:h-12 px-6 sm:px-6 rounded-xl bg-red-600 text-white font-semibold hover:bg-red-700 transition-colors shadow-lg shadow-red-500/25"
              >
                Sign Out
              </AlertDialogAction>
            </AlertDialogFooter>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Landing page route - behavior depends on domain */}
          <Route path="/" element={
            isLandingDomain() ? (
              // On landing domain (www), always show landing page
              <LandingPage />
            ) : (
              // On app domain, redirect to dashboard if logged in, otherwise show login
              isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
            )
          } />

          {/* Public routes - always accessible */}
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/affiliate-terms" element={<AffiliateTerms />} />
          <Route path="/affiliate-apply" element={<AffiliateApply />} />
          <Route path="/transparent-accounting" element={<TransparentAccounting />} />
          <Route path="/help" element={<HelpCenter />} />
          <Route path="/help/:categoryId" element={<HelpCenter />} />
          <Route path="/help/:categoryId/:articleId" element={<HelpCenter />} />
          <Route path="/public/cohost-report/:token" element={<PublicReport />} />
          <Route path="/invite/accept" element={<InviteAccept />} />
          <Route path="/sign/:token" element={<PublicSigningPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Landing page preview route - for local development testing */}
          <Route path="/landing" element={<LandingPage />} />

          {/* Login route - only when not authenticated */}
          <Route path="/login" element={
            isLandingDomain() ? (
              // On landing domain, redirect login requests to app domain
              <ExternalRedirect to="https://app.turnover-manager.com/login" />
            ) : (
              isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login onLogin={handleLogin} />
            )
          } />

          {/* Protected routes - require authentication */}
          <Route path="/app" element={
            !isAuthenticated ? <Navigate to="/login" replace /> :
              needsOnboarding ? <OnboardingSimple onComplete={handleOnboardingComplete} /> :
                <Navigate to="/dashboard" replace />
          } />

          <Route path="/dashboard" element={
            !isAuthenticated ? <Navigate to="/login" replace /> :
              needsOnboarding ? <OnboardingSimple onComplete={handleOnboardingComplete} /> :
                (!hasAccess ? <SubscriptionRequired /> :
                  <AuthenticatedLayout user={user} onLogout={handleLogout} isCollapsed={isSidebarCollapsed} onToggleCollapse={handleSidebarToggle}>
                      <Dashboard />
                    </AuthenticatedLayout>)
          } />

          <Route path="/vehicle-management" element={
            !isAuthenticated ? <Navigate to="/login" replace /> :
              needsOnboarding ? <OnboardingSimple onComplete={handleOnboardingComplete} /> :
                (!hasAccess ? <SubscriptionRequired /> :
                  <AuthenticatedLayout user={user} onLogout={handleLogout} isCollapsed={isSidebarCollapsed} onToggleCollapse={handleSidebarToggle}>
                      <VehicleManagement />
                    </AuthenticatedLayout>)
          } />

          {/* System Unification: Redirect old /upcoming-maintenance to dashboard operations panel */}
          <Route path="/upcoming-maintenance" element={
            <Navigate to="/dashboard?panel=operations" replace />
          } />

          <Route path="/co-hosts" element={
            !isAuthenticated ? <Navigate to="/login" replace /> :
              needsOnboarding ? <OnboardingSimple onComplete={handleOnboardingComplete} /> :
                (!hasAccess ? <SubscriptionRequired /> :
                  <AuthenticatedLayout user={user} onLogout={handleLogout} isCollapsed={isSidebarCollapsed} onToggleCollapse={handleSidebarToggle}>
                      <CoHostManagement />
                    </AuthenticatedLayout>)
          } />
          <Route path="/co-hosts/:id" element={
            !isAuthenticated ? <Navigate to="/login" replace /> :
              needsOnboarding ? <OnboardingSimple onComplete={handleOnboardingComplete} /> :
                (!hasAccess ? <SubscriptionRequired /> :
                  <AuthenticatedLayout user={user} onLogout={handleLogout} isCollapsed={isSidebarCollapsed} onToggleCollapse={handleSidebarToggle}>
                      <CoHostDetail />
                    </AuthenticatedLayout>)
          } />

          {/* Monday.com Replacement Routes - Per VEHICLES_PAGE_REFACTORING_PLAN.md */}
          {/* Operations routes redirect to unified Operations tab */}
          <Route path="/operations/today" element={<Navigate to="/vehicle-management" replace />} />
          <Route path="/checkout-pipeline" element={<Navigate to="/vehicle-management" replace />} />
          <Route path="/tasks" element={<Navigate to="/vehicle-management" replace />} />
          {/* Compliance routes redirect to unified Fleet tab */}
          <Route path="/compliance" element={<Navigate to="/vehicle-management?tab=fleet" replace />} />
          <Route path="/vehicles-crm" element={<Navigate to="/vehicle-management?tab=fleet" replace />} />
          {/* cohost-pipeline route redirects to /co-hosts - functionality integrated into main Co-Hosts page */}
          <Route path="/cohost-pipeline" element={<Navigate to="/co-hosts" replace />} />

          <Route path="/finance-hub" element={
            !isAuthenticated ? <Navigate to="/login" replace /> :
              needsOnboarding ? <OnboardingSimple onComplete={handleOnboardingComplete} /> :
                (!hasAccess ? <SubscriptionRequired /> :
                  <AuthenticatedLayout user={user} onLogout={handleLogout} isCollapsed={isSidebarCollapsed} onToggleCollapse={handleSidebarToggle}>
                      <CohostFinanceHub getAuthToken={() => user?.getIdToken?.()} />
                    </AuthenticatedLayout>)
          } />

          <Route path="/expenses" element={
            !isAuthenticated ? <Navigate to="/login" replace /> :
              needsOnboarding ? <OnboardingSimple onComplete={handleOnboardingComplete} /> :
                (!hasAccess ? <SubscriptionRequired /> :
                  <AuthenticatedLayout user={user} onLogout={handleLogout} isCollapsed={isSidebarCollapsed} onToggleCollapse={handleSidebarToggle}>
                      <ExpenseBreakdownPage />
                    </AuthenticatedLayout>)
          } />

          <Route path="/earnings" element={
            !isAuthenticated ? <Navigate to="/login" replace /> :
              needsOnboarding ? <OnboardingSimple onComplete={handleOnboardingComplete} /> :
                (!hasAccess ? <SubscriptionRequired /> :
                  <AuthenticatedLayout user={user} onLogout={handleLogout} isCollapsed={isSidebarCollapsed} onToggleCollapse={handleSidebarToggle}>
                      <EarningsBreakdownPage />
                    </AuthenticatedLayout>)
          } />

          <Route path="/profit" element={
            !isAuthenticated ? <Navigate to="/login" replace /> :
              needsOnboarding ? <OnboardingSimple onComplete={handleOnboardingComplete} /> :
                (!hasAccess ? <SubscriptionRequired /> :
                  <AuthenticatedLayout user={user} onLogout={handleLogout} isCollapsed={isSidebarCollapsed} onToggleCollapse={handleSidebarToggle}>
                      <NetProfitBreakdownPage />
                    </AuthenticatedLayout>)
          } />

          <Route path="/bank-transactions" element={
            !isAuthenticated ? <Navigate to="/login" replace /> :
              needsOnboarding ? <OnboardingSimple onComplete={handleOnboardingComplete} /> :
                (!hasAccess ? <SubscriptionRequired /> :
                  <AuthenticatedLayout user={user} onLogout={handleLogout} isCollapsed={isSidebarCollapsed} onToggleCollapse={handleSidebarToggle}>
                      <BankTransactionsPage />
                    </AuthenticatedLayout>)
          } />

          {/* Redirect old payout-verification URL to Finance Hub */}
          <Route path="/payout-verification" element={<Navigate to="/finance-hub" replace />} />

          <Route path="/roi-calculator" element={
            // ROI Calculator is PUBLIC - accessible without login for "Planning to Buy" mode
            // Logged-in users get full dashboard experience, others get standalone calculator
            !isAuthenticated ? (
              <div className="min-h-screen bg-slate-50 py-8 px-4">
                <ROICalculator />
              </div>
            ) : needsOnboarding ? <OnboardingSimple onComplete={handleOnboardingComplete} /> :
                (!hasAccess ? <SubscriptionRequired /> :
                  <AuthenticatedLayout user={user} onLogout={handleLogout} isCollapsed={isSidebarCollapsed} onToggleCollapse={handleSidebarToggle}>
                      <ROICalculator />
                    </AuthenticatedLayout>)
          } />

          <Route path="/roi/shared/:token" element={
            <div className="min-h-screen bg-slate-50">
              <SharedROIRedirect />
            </div>
          } />

          <Route path="/migration-service" element={
            !isAuthenticated ? <Navigate to="/login" replace /> :
              needsOnboarding ? <OnboardingSimple onComplete={handleOnboardingComplete} /> :
                (!hasAccess ? <SubscriptionRequired /> :
                  <AuthenticatedLayout user={user} onLogout={handleLogout} isCollapsed={isSidebarCollapsed} onToggleCollapse={handleSidebarToggle}>
                      <MigrationServicePage />
                    </AuthenticatedLayout>)
          } />

          <Route path="/settings/*" element={
            !isAuthenticated ? <Navigate to="/login" replace /> :
              needsOnboarding ? <OnboardingSimple onComplete={handleOnboardingComplete} /> :
                (!hasAccess ? <SubscriptionRequired /> :
                  <SettingsRouter user={user} onLogout={handleLogout} />)
          } />

          <Route path="/affiliate-dashboard" element={
            !isAuthenticated ? <Navigate to="/login" replace /> :
              <AffiliateDashboard />
          } />

          <Route path="/admin/affiliates" element={
            !isAuthenticated ? <Navigate to="/login" replace /> :
              <AffiliateAdmin />
          } />

          <Route path="/pricing" element={
            isAuthenticated ?
              needsOnboarding ? <OnboardingSimple onComplete={handleOnboardingComplete} /> :
                <AuthenticatedLayout user={user} onLogout={handleLogout} isCollapsed={isSidebarCollapsed} onToggleCollapse={handleSidebarToggle}>
                    <PricingPage isAuthenticated={true} currentTier={user?.planTier || 'trial'} />
                  </AuthenticatedLayout>
              : <PricingPage isAuthenticated={false} />
          } />

          {/* Legacy route redirects for backward compatibility */}
          <Route path="/select-plan" element={<Navigate to="/pricing" replace />} />
          <Route path="/cohost-finance" element={<Navigate to="/finance-hub" replace />} />
          <Route path="/cohosts" element={<Navigate to="/co-hosts" replace />} />

          {/* Catch-all - redirect based on auth state */}
          <Route path="*" element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
          } />
        </Routes>
      </Suspense>
      <Toaster />
    </div>
    </PermissionProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryProvider>
        <ThemeProvider>
          <PreferencesProvider>
            <BrowserRouter>
              <AccountProvider>
                <ScrollToTop />
                <AppContent />
              </AccountProvider>
            </BrowserRouter>
          </PreferencesProvider>
        </ThemeProvider>
      </QueryProvider>
    </ErrorBoundary>
  );
}

export default App;
